import React from 'react';

import { useTranslation } from '@lyearn/i18n';
import { cn } from '@lyearn/style';
import { Box, Input, Stack, Typography } from '@lyearn/ui';
import { DialogComponent } from '@/components/DialogComponent';

import { BOTTOM_SHEET_CLASSES, DIALOG_CLASSES } from '../../consts';
import { feedbackInScaleFiveConfig, FeedbackRateInFiveScale } from '../MoodEmojiList/config';
import { EmojiEnum } from '../MoodEmojiList/consts';
import MoodEmoji from '../MoodEmojiList/MoodEmoji';
import MoodEmojiContainer from '../MoodEmojiList/MoodEmojiContainer';
import { renderDialogFooter } from '../renderers';
import { inputClasses } from './styles';

interface FeedbackDialogProps {
  onClose: () => void;
  onSubmit: (userFeedback: string) => void;
  questionText?: string | null;
  questionDescription?: string | null;
  emoji: EmojiEnum;
  isRatingOnFiveScale?: boolean;
}

const FeedbackDialog = (props: FeedbackDialogProps) => {
  const {
    onClose,
    questionText,
    questionDescription,
    emoji,
    onSubmit: _onSubmit,
    isRatingOnFiveScale,
  } = props;
  const { t } = useTranslation();
  const [userFeedback, setUserFeedback] = React.useState('');
  const [error, setError] = React.useState(false);
  const onInputChange = React.useCallback(
    (text: string) => {
      setUserFeedback(text);
      if (error && text) {
        setError(false);
      }
    },
    [error, setUserFeedback],
  );

  const onSubmit = React.useCallback(() => {
    if (userFeedback) {
      _onSubmit(userFeedback);
    }
    onClose();
  }, [_onSubmit, onClose, userFeedback]);

  return (
    <DialogComponent
      open
      bottomSheetClasses={BOTTOM_SHEET_CLASSES}
      classes={DIALOG_CLASSES}
      showCloseIcon={false}
      onClose={onClose}>
      <Stack className={cn('py-4 px-8')} direction="col">
        <Stack
          alignItems="center"
          className="xs:border-b-hairline xs:border-divider xs:pb-16"
          direction="row">
          {isRatingOnFiveScale ? (
            <Box style={{ fontSize: 40 }}>
              {feedbackInScaleFiveConfig[emoji as unknown as FeedbackRateInFiveScale]?.label}
            </Box>
          ) : (
            <MoodEmojiContainer>
              <MoodEmoji emojiId={emoji} hoverScaleFactor={1} isActive={true} scaleFactor={1} />
            </MoodEmojiContainer>
          )}

          <Stack className="ms-8 xs:ms-16" direction="col">
            {questionText ? <Typography variant="heading-xs">{questionText}</Typography> : null}
            <Typography color="text-text-secondary" variant="body-short-02">
              {questionDescription}
            </Typography>
          </Stack>
        </Stack>
        <Input
          autoFocus
          multiline
          className={cn(
            'body-short-01 mt-24',
            {
              [`${inputClasses}`]: error,
            },
            'xs:max-h-128 xs:border-0 xs:ps-0 xs:pe-0',
          )}
          maxRows={5}
          placeholder={t('Common:ShareFeedback')}
          rows={5}
          size={'large'}
          value={userFeedback}
          onChangeText={onInputChange}
        />
        <Typography className="m-8 h-16" color="text-text-error" variant="body-short-03">
          {error ? t('Common:Error.CantBeEmpty') : ''}
        </Typography>
        {renderDialogFooter({ t, onSubmit, onClose })}
      </Stack>
    </DialogComponent>
  );
};

export default FeedbackDialog;
